import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";

import FlightIssuanceService from "@/modules/FlightIssuance/services/flightIssuance.service";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import {
  FETCH_FLIGHT_PNR_PAYLOAD,
  ISSUE_TICKET_PAYLOAD,
  PRICE_QUOTE_FARE_CALCULATION_PAYLOAD,
} from "@/core/types/flight";
import notificationService from "@/core/services/notification.service";
import { IFlightIssuanceState } from "@/core/interfaces/IFlightIssuance.interface";
import { CACHE_NAMES } from "@/core/constants";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { ApplyFlightPromoCodeDTO } from "@/modules/FlightIssuance/dtos/flightIssuance.dto";
import { StoreState } from "@/store/type";

const actions: ActionTree<IFlightIssuanceState, StoreState> = {
  // ****** Fetch PNR ******
  async fetchPNR(
    context: ActionContext<IFlightIssuanceState, StoreState>,
    params: FETCH_FLIGHT_PNR_PAYLOAD,
  ) {
    context.commit("resetPNRDetails");
    try {
      context.commit("fetchingPNR", true);
      const flightIssuanceService = new FlightIssuanceService();
      const response: ICoreResponse =
        await flightIssuanceService.fetchPNR(params);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("fetchedPNRDetail", response.data.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("fetchingPNR", false);
    }
  },

  async fetchPNRV3(
    context: ActionContext<IFlightIssuanceState, StoreState>,
    params: FETCH_FLIGHT_PNR_PAYLOAD,
  ) {
    context.commit("resetPNRDetails");
    try {
      context.commit("fetchingPNR", true);
      const flightIssuanceService = new FlightIssuanceService();
      const response: ICoreResponse =
        await flightIssuanceService.fetchPNRV3(params);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("fetchedPNRDetailV3", response.data.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("fetchingPNR", false);
    }
  },

  async calculatePQFare(
    context: ActionContext<IFlightIssuanceState, StoreState>,
    params: PRICE_QUOTE_FARE_CALCULATION_PAYLOAD,
  ) {
    context.commit("resetCalculatedFare");
    try {
      context.commit("setCalculatedFareV3", null);
      context.commit("calculatingFareV3", true);
      const flightIssuanceService = new FlightIssuanceService();
      const response: ICoreResponse =
        await flightIssuanceService.calculatePriceQuoteFare(params);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setCalculatedFareV3", response.data.data);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("calculatingFareV3", false);
    }
  },

  // ****** Issue Ticket ******
  async issueTicket(
    context: ActionContext<IFlightIssuanceState, StoreState>,
    params: ISSUE_TICKET_PAYLOAD,
  ) {
    try {
      context.commit("isIssueTicketLoading", true);
      const flightIssuanceService = new FlightIssuanceService();
      const response: ICoreResponse =
        await flightIssuanceService.issueTicket(params);
      if (
        response.success &&
        response.data?.success &&
        response.status === StatusCodes.OK
      ) {
        await caches.delete(CACHE_NAMES.flightsBookings);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.data?.message || "Ticket is successfully issued";
        notificationService.triggerNotification();
        context.commit("isTicketIssued", true);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreResponse;

      const errorMsg = exception.data?.error;
      if (
        errorMsg === "MISSING_ISSUANCE_MANDATORY_FIELDS" ||
        errorMsg === "CoreError.MISSING_ISSUANCE_MANDATORY_FIELDS"
      ) {
        context.commit("setMissingFieldsError", true);
      }

      const errorMessage =
        exception.data?.message || exception.message || exception.error;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        errorMessage || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("isIssueTicketLoading", false);
    }
  },

  // ****** Fetch Sales Users List For Approval *****
  async fetchSalesUsersListForApproval(
    context: ActionContext<IFlightIssuanceState, StoreState>,
  ) {
    try {
      const flightIssuanceService = new FlightIssuanceService();
      const response: ICoreResponse =
        await flightIssuanceService.getSalesUsersListForIssanceApproval();
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("saveSalesUsersListForApproval", response.data);
      } else {
        throw response;
      }
    } catch (err) {
      console.error("Error in fetchSalesUsersListForApproval:", err);
    }
  },

  // ****** Apply Flight Promo Code *****
  async applyFlightPromoCode(
    context: ActionContext<IFlightIssuanceState, StoreState>,
    params: ApplyFlightPromoCodeDTO,
  ) {
    try {
      context.commit("setApplyFlightPromoLoading", true);
      context.commit("setApplyFlightPromoResponse", null);

      const flightIssuanceService = new FlightIssuanceService();
      const response: ICoreResponse =
        await flightIssuanceService.applyFlightsPromoCode(params);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setApplyFlightPromoResponse", response.data?.data);
        context.commit("setApplyFlightPromoLoading", false);
      } else {
        context.commit("setApplyFlightPromoLoading", false);
        throw response;
      }
    } catch (err) {
      console.error("Error in applyFlightPromoCode:", err);
    }
  },

  async applyFlightPromoCodeV2(
    context: ActionContext<IFlightIssuanceState, StoreState>,
    params: ApplyFlightPromoCodeDTO,
  ) {
    try {
      context.commit("setApplyFlightPromoLoading", true);
      context.commit("setApplyFlightPromoResponse", null);

      const flightIssuanceService = new FlightIssuanceService();
      const response: ICoreResponse =
        await flightIssuanceService.applyFlightsPromoCodeV2(params);
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setApplyFlightPromoResponse", response.data?.data);
        context.commit("setApplyFlightPromoLoading", false);
      } else {
        context.commit("setApplyFlightPromoLoading", false);
        throw response;
      }
    } catch (err) {
      console.error("Error in applyFlightPromoCode:", err);
    }
  },
};

export default actions;
